/**
 * GraphTest page - Test and demonstration page for GraphVisualization component.
 * This page is for development and testing purposes only.
 */

import { FC, useRef, useState } from 'react';
import { Box, Typography, Card, CardContent, Button, ButtonGroup } from '@mui/material';
import { GraphVisualization, GraphControls } from '@/components/Graph';
import type { GraphData, GraphNode, GraphEdge } from '@/types/entities';

/**
 * Generate sample graph data for testing
 */
function generateSampleData(): GraphData {
  const nodes: GraphNode[] = [
    // Switches
    {
      id: 'switch1',
      label: 'SW-CORE-01',
      type: 'switch',
      data: {
        hostname: 'SW-CORE-01',
        vendor: 'Cisco',
        model: 'Catalyst 9300',
        managementIp: '192.168.1.1',
      },
    },
    {
      id: 'switch2',
      label: 'SW-ACCESS-01',
      type: 'switch',
      data: {
        hostname: 'SW-ACCESS-01',
        vendor: 'Cisco',
        model: 'Catalyst 9200',
        managementIp: '192.168.1.2',
      },
    },
    // VLANs
    {
      id: 'vlan10',
      label: 'VLAN 10',
      type: 'vlan',
      data: {
        vlanID: 10,
        name: 'Engineering',
        status: 'active',
      },
    },
    {
      id: 'vlan20',
      label: 'VLAN 20',
      type: 'vlan',
      data: {
        vlanID: 20,
        name: 'Sales',
        status: 'active',
      },
    },
    {
      id: 'vlan30',
      label: 'VLAN 30',
      type: 'vlan',
      data: {
        vlanID: 30,
        name: 'Management',
        status: 'active',
      },
    },
    // Interfaces - SW-CORE-01
    {
      id: 'int1',
      label: 'Gi1/0/1',
      type: 'interface',
      data: {
        uniqueId: 'switch1-Gi1/0/1',
        name: 'GigabitEthernet1/0/1',
        mode: 'access',
        status: 'up',
        description: 'Engineering Workstation',
      },
    },
    {
      id: 'int2',
      label: 'Gi1/0/2',
      type: 'interface',
      data: {
        uniqueId: 'switch1-Gi1/0/2',
        name: 'GigabitEthernet1/0/2',
        mode: 'access',
        status: 'up',
        description: 'Sales Workstation',
      },
    },
    {
      id: 'int3',
      label: 'Gi1/0/24',
      type: 'interface',
      data: {
        uniqueId: 'switch1-Gi1/0/24',
        name: 'GigabitEthernet1/0/24',
        mode: 'trunk',
        status: 'up',
        description: 'Trunk to SW-ACCESS-01',
      },
    },
    // Interfaces - SW-ACCESS-01
    {
      id: 'int4',
      label: 'Gi1/0/1',
      type: 'interface',
      data: {
        uniqueId: 'switch2-Gi1/0/1',
        name: 'GigabitEthernet1/0/1',
        mode: 'access',
        status: 'up',
        description: 'Engineering Device',
      },
    },
    {
      id: 'int5',
      label: 'Gi1/0/2',
      type: 'interface',
      data: {
        uniqueId: 'switch2-Gi1/0/2',
        name: 'GigabitEthernet1/0/2',
        mode: 'access',
        status: 'up',
        description: 'Sales Device',
      },
    },
    {
      id: 'int6',
      label: 'Gi1/0/24',
      type: 'interface',
      data: {
        uniqueId: 'switch2-Gi1/0/24',
        name: 'GigabitEthernet1/0/24',
        mode: 'trunk',
        status: 'up',
        description: 'Uplink to Core',
      },
    },
  ];

  const edges: GraphEdge[] = [
    // HAS_INTERFACE relationships
    {
      id: 'e1',
      source: 'switch1',
      target: 'int1',
      type: 'HAS_INTERFACE',
      label: '',
    },
    {
      id: 'e2',
      source: 'switch1',
      target: 'int2',
      type: 'HAS_INTERFACE',
      label: '',
    },
    {
      id: 'e3',
      source: 'switch1',
      target: 'int3',
      type: 'HAS_INTERFACE',
      label: '',
    },
    {
      id: 'e4',
      source: 'switch2',
      target: 'int4',
      type: 'HAS_INTERFACE',
      label: '',
    },
    {
      id: 'e5',
      source: 'switch2',
      target: 'int5',
      type: 'HAS_INTERFACE',
      label: '',
    },
    {
      id: 'e6',
      source: 'switch2',
      target: 'int6',
      type: 'HAS_INTERFACE',
      label: '',
    },
    // MEMBER_OF relationships (access ports)
    {
      id: 'e7',
      source: 'int1',
      target: 'vlan10',
      type: 'MEMBER_OF',
      label: 'member',
    },
    {
      id: 'e8',
      source: 'int2',
      target: 'vlan20',
      type: 'MEMBER_OF',
      label: 'member',
    },
    {
      id: 'e9',
      source: 'int4',
      target: 'vlan10',
      type: 'MEMBER_OF',
      label: 'member',
    },
    {
      id: 'e10',
      source: 'int5',
      target: 'vlan20',
      type: 'MEMBER_OF',
      label: 'member',
    },
    // TRUNKS_VLAN relationships (trunk ports)
    {
      id: 'e11',
      source: 'int3',
      target: 'vlan10',
      type: 'TRUNKS_VLAN',
      label: 'trunk',
    },
    {
      id: 'e12',
      source: 'int3',
      target: 'vlan20',
      type: 'TRUNKS_VLAN',
      label: 'trunk',
    },
    {
      id: 'e13',
      source: 'int3',
      target: 'vlan30',
      type: 'TRUNKS_VLAN',
      label: 'native',
      data: { native: true },
    },
    {
      id: 'e14',
      source: 'int6',
      target: 'vlan10',
      type: 'TRUNKS_VLAN',
      label: 'trunk',
    },
    {
      id: 'e15',
      source: 'int6',
      target: 'vlan20',
      type: 'TRUNKS_VLAN',
      label: 'trunk',
    },
    {
      id: 'e16',
      source: 'int6',
      target: 'vlan30',
      type: 'TRUNKS_VLAN',
      label: 'native',
      data: { native: true },
    },
  ];

  return { nodes, edges };
}

export const GraphTest: FC = () => {
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const [graphData] = useState<GraphData>(generateSampleData());
  const [centerNode, setCenterNode] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState<string>('');

  const handleNodeClick = (node: GraphNode) => {
    setSelectedInfo(`Node clicked: ${node.label} (${node.type})`);
    // eslint-disable-next-line no-console
    console.log('Node clicked:', node);
  };

  const handleEdgeClick = (edge: GraphEdge) => {
    setSelectedInfo(`Edge clicked: ${edge.label} (${edge.type})`);
    // eslint-disable-next-line no-console
    console.log('Edge clicked:', edge);
  };

  const handleTestLoading = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 2000);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Graph Visualization Test
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Testing FRONTEND-008: Cytoscape.js integration with sample network topology.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test Controls
          </Typography>
          <ButtonGroup variant="outlined" sx={{ mb: 2 }}>
            <Button onClick={() => setCenterNode('switch1')}>Center Switch 1</Button>
            <Button onClick={() => setCenterNode('vlan10')}>Center VLAN 10</Button>
            <Button onClick={() => setCenterNode(undefined)}>No Center</Button>
            <Button onClick={handleTestLoading}>Test Loading</Button>
          </ButtonGroup>

          {selectedInfo && (
            <Typography variant="body2" color="primary">
              {selectedInfo}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Network Topology Graph
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Interactive features: Zoom (mouse wheel), Pan (drag canvas), Drag nodes,
            Hover for highlights, Click nodes/edges
          </Typography>

          <Box sx={{ position: 'relative' }} ref={graphContainerRef}>
            <GraphVisualization
              data={graphData}
              height="600px"
              layout="cose"
              onNodeClick={handleNodeClick}
              onEdgeClick={handleEdgeClick}
              centerNodeId={centerNode}
              loading={loading}
            />
            {!loading && graphData.nodes.length > 0 && (
              <GraphControls containerRef={graphContainerRef} />
            )}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Graph Statistics
          </Typography>
          <Typography variant="body2">
            Nodes: {graphData.nodes.length} (
            {graphData.nodes.filter((n) => n.type === 'switch').length} switches,{' '}
            {graphData.nodes.filter((n) => n.type === 'vlan').length} VLANs,{' '}
            {graphData.nodes.filter((n) => n.type === 'interface').length} interfaces)
          </Typography>
          <Typography variant="body2">
            Edges: {graphData.edges.length}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
